import React, { FC, useEffect, useMemo, useState } from "react";

import { RichText } from "../../../common/RichText";
import { IOptionValues } from "../../../models/catalogue.interfaces";
import { useAppSelector } from "../../reducers";
import { TabFeatureAttributeBlockAttributeTabButton } from "../elements/TabFeatureAttributeBlockAttributeTabButton";
import { TabFeatureAttributeBlockAttributeTabContent } from "../elements/TabFeatureAttributeBlockAttributeTabContent";
import { ITabFeatureAttributeBlock } from "../models.interfaces";
import { getOptionValueByIdx } from "../utils";

import styles from "./TabFeatureAttributeBlock.module.scss";

export const TabFeatureAttributeBlock: FC<ITabFeatureAttributeBlock> = (
    props,
) => {
    const optionValues = useAppSelector(
        (state) => state.configurator.ui.optionValues,
    );
    const [selectedAttrValue, setSelectedAttrValue] = useState<string | null>(
        null,
    );

    // Create a set of valid attribute values from props.tabs
    const validValues = useMemo(
        () => props.tabs.map((tab) => tab.attribute_value),
        [props.tabs],
    );
    // Compute selected attribute value
    const computedSelectedAttrValue = useMemo(() => {
        if (selectedAttrValue && validValues.includes(selectedAttrValue)) {
            return selectedAttrValue;
        }
        if (props.tabs.length > 0) {
            return props.tabs[0].attribute_value;
        }
        return null;
    }, [selectedAttrValue, validValues, props.tabs]);

    // Get selected attribute from redux
    const getSelectedAttributeFromRedux = (optionValues: IOptionValues) => {
        const selectedReduxValues = optionValues[props.attribute];
        if (!selectedReduxValues) {
            return;
        }
        return getOptionValueByIdx(selectedReduxValues, 0);
    };

    // Sync with redux on mount and when optionValues change
    useEffect(() => {
        const selectedReduxValue = getSelectedAttributeFromRedux(optionValues);
        setSelectedAttrValue(selectedReduxValue || null);
    }, [optionValues]);

    // Check if tabs are defined
    if (!props.tabs) {
        return null;
    }

    return (
        <div className={`u-flex-container ${styles.root}`}>
            <header className={styles.header}>
                <RichText html={props.header_content} />
            </header>
            {/* TODO(i18n): Use t tagged template for aria-label: aria-label={t`Tab Feature Attribute Block - Tab List`} */}
            <div
                className={styles.tablist}
                role="tablist"
                aria-label="Tab Feature Attribute Block - Tab List"
            >
                {props.tabs.map((attr, i: number) => (
                    <TabFeatureAttributeBlockAttributeTabButton
                        key={attr.attribute_value}
                        idx={i}
                        tab={attr}
                        onClick={() => {
                            setSelectedAttrValue(attr.attribute_value);
                        }}
                        isSelected={
                            attr.attribute_value === computedSelectedAttrValue
                        }
                    />
                ))}
                <div className={styles.tablistBar}></div>
            </div>
            <div className={styles.content}>
                {props.tabs.map((attr, i: number) => (
                    <TabFeatureAttributeBlockAttributeTabContent
                        key={attr.attribute_value}
                        attribute={props.attribute}
                        idx={i}
                        tab={attr}
                        isSelected={
                            attr.attribute_value === computedSelectedAttrValue
                        }
                    />
                ))}
            </div>
        </div>
    );
};
